import * as React from "react";
import { SearchFieldStyle, SearchFieldVariant } from "./search-field-style";
import AppIcon from "common/app-icon";
import { withDefault } from "collection-utils";
import { GlideFC } from "lib/glide-fc";

interface Props {
    value: string;
    onChange: (value: string) => void;
    onEnter?: () => void;
    placeholder: string;
    variant?: SearchFieldVariant;
}

export const SearchField: GlideFC<Props> = p => {
    const { onEnter, value, onChange, variant, placeholder } = p;

    const [isFocused, setIsFocused] = React.useState(false);

    const inputRef = React.useRef<HTMLInputElement>(null);

    const close = React.useCallback(() => {
        onChange("");
        inputRef.current?.blur();
        setIsFocused(false);
    }, [onChange]);

    const onKeyUp = React.useCallback(
        (evt: React.KeyboardEvent<HTMLDivElement>) => {
            if (evt.key === "Escape" && isFocused) {
                close();
            } else if (evt.key === "Enter" && onEnter !== undefined) {
                onEnter();
            }
        },
        [close, isFocused, onEnter]
    );

    const onFocus = React.useCallback(() => {
        setIsFocused(true);
    }, []);

    const onBlur = React.useCallback(() => {
        if (value === "") {
            setIsFocused(false);
        }
    }, [value]);

    const onMouseDown = React.useCallback((e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
    }, []);

    const onInputChange = React.useCallback(
        (evnt: React.ChangeEvent<HTMLInputElement>) => {
            evnt.stopPropagation();
            onChange(evnt.target.value);
        },
        [onChange]
    );

    const variantOrDefault = withDefault(variant, "default");
    const showClearButton = value !== "";

    return (
        <SearchFieldStyle variant={variantOrDefault} showClearButton={showClearButton} isFocused={isFocused}>
            <div className="sf-slider">
                <AppIcon className="sf-icon" icon="01-25-search-alternate" size={16} />
                <input
                    className="sf-input"
                    ref={inputRef}
                    placeholder={placeholder}
                    onKeyUp={onKeyUp}
                    value={value}
                    onFocus={onFocus}
                    onBlur={onBlur}
                    onChange={onInputChange}
                />
                {showClearButton && (
                    <button onClick={close} onMouseDown={onMouseDown} className="sf-close-button">
                        <AppIcon icon="01-43-remove-circle" size={16} />
                    </button>
                )}
            </div>
        </SearchFieldStyle>
    );
};
